// src/pages/Admin/area-notifications/index.tsx
//
// Page integration for the area-broadcast push notification flow.
// Glues together ComposeForm + PreviewPanel + HistoryTable behind a small
// state machine ({ kind: "compose" } | { kind: "preview", draft, preview }).
//
// Mounted as a tab from `AdminPage.tsx` (the admin uses an in-page Tab pattern
// rather than React Router routes); env is local state per tab matching the
// pattern used by `BusinessPushNotificationsTab`.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { acquireApiToken } from '@/auth/acquireApiToken';
import {
  Env,
  ENV_COLORS,
  ErrorBanner,
  Spinner,
  getAdminApiUrl,
} from '../shared';
import {
  AreaPreviewRequest,
  AreaPreviewResponse,
  SendAreaBroadcastRequest,
  useAreaPreview,
  useSendAreaBroadcast,
} from './api';
import {
  ComposeForm,
  DraftBroadcast,
  MarketOption,
} from './ComposeForm';
import { PreviewPanel } from './PreviewPanel';
import { HistoryTable } from './HistoryTable';

// ─── Local types ──────────────────────────────────────────────────────────────

type Mode =
  | { kind: 'compose' }
  | { kind: 'preview'; draft: DraftBroadcast; preview: AreaPreviewResponse };

const KM_TO_MI = 0.621371;

// Fallback markets shown when GET /admin/markets isn't deployed (e.g. dev).
// IDs are synthetic — see HARDCODED_PREFIX. Match the V54 seed data so the
// dropdown matches what's actually in the database.
const HARDCODED_PREFIX = '__hardcoded:';
const HARDCODED_FALLBACK_MARKETS: MarketOption[] = [
  {
    id: `${HARDCODED_PREFIX}asu-tempe`,
    name: 'ASU Tempe',
    center_lat: 33.4242,
    center_lng: -111.9281,
    radius_mi: +(15 * KM_TO_MI).toFixed(2), // 15 km → 9.32 mi
  },
];

// ─── Inline markets hook ──────────────────────────────────────────────────────
//
// Mirrors the MSAL pattern used in `api.ts`. Hits GET /admin/markets (the
// existing Marketplace endpoint) and maps it to the `MarketOption` shape that
// ComposeForm expects. Markets without center_lat/lng are filtered out since
// the Compose flow needs valid coordinates to seed the map.

interface MarketsListResponse {
  items: Array<{
    id: string;
    name: string;
    center_lat?: number | null;
    center_lng?: number | null;
    radius_km?: number | null;
  }>;
}

function useMarkets(env: Env): {
  markets: MarketOption[];
  loading: boolean;
  error: string | null;
} {
  const isAuthenticated = useIsAuthenticated();
  const { instance, accounts } = useMsal();
  const accountsRef = useRef(accounts);
  accountsRef.current = accounts;

  const [markets, setMarkets] = useState<MarketOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    const fetchMarkets = async () => {
      const apiUrl = getAdminApiUrl(env);
      if (!apiUrl) {
        if (isMounted) {
          setError('API URL is not configured for this environment');
          setLoading(false);
          setMarkets([]);
        }
        return;
      }

      if (isMounted) {
        setLoading(true);
        setError(null);
      }

      try {
        let token: string | null = null;
        if (isAuthenticated && accountsRef.current.length > 0) {
          token = await acquireApiToken(instance, accountsRef.current[0]);
        }

        const response = await fetch(`${apiUrl}/admin/markets`, {
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });

        if (!response.ok) {
          // Endpoint not deployed in this env — fall back to a hardcoded list
          // that matches what's seeded in the dev DB, so the dropdown is usable.
          if (isMounted) {
            setMarkets(HARDCODED_FALLBACK_MARKETS);
            setError(null);
          }
          return;
        }

        const data: MarketsListResponse = await response.json();
        const mapped: MarketOption[] = (data.items ?? [])
          .filter(
            (m) =>
              typeof m.center_lat === 'number' &&
              typeof m.center_lng === 'number',
          )
          .map((m) => ({
            id: m.id,
            name: m.name,
            center_lat: m.center_lat as number,
            center_lng: m.center_lng as number,
            radius_mi:
              typeof m.radius_km === 'number' && m.radius_km > 0
                ? +(m.radius_km * KM_TO_MI).toFixed(2)
                : 5,
          }));

        if (isMounted) {
          // A successful response with zero valid markets means the env
          // genuinely has none configured — respect that and show an empty
          // dropdown rather than masking it with a synthetic Tempe entry.
          setMarkets(mapped);
        }
      } catch (err: any) {
        if (isMounted) {
          setError(null);
          setMarkets(HARDCODED_FALLBACK_MARKETS);
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchMarkets();
    return () => {
      isMounted = false;
    };
  }, [env, isAuthenticated, instance]);

  return { markets, loading, error };
}

// ─── Page component ───────────────────────────────────────────────────────────

export default function AreaNotificationsPage(): React.ReactElement {
  const [env, setEnv] = useState<Env>('dev');
  const [mode, setMode] = useState<Mode>({ kind: 'compose' });
  const [historyKey, setHistoryKey] = useState(0);
  const [sendError, setSendError] = useState<string | null>(null);
  const activePanelRef = useRef<HTMLDivElement>(null);

  const { markets, loading: marketsLoading } = useMarkets(env);
  const { loading: previewLoading, error: previewError, preview } =
    useAreaPreview();
  const { send } = useSendAreaBroadcast();

  // Scroll the active panel into view when entering preview mode so the
  // user lands on the audience summary instead of staying anchored at the
  // bottom of the (now-replaced) compose form.
  useEffect(() => {
    if (mode.kind === 'preview' && activePanelRef.current) {
      activePanelRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, [mode.kind]);

  // Reset to compose state and clear errors when the env changes — preview &
  // history are env-scoped on the backend.
  const handleEnvChange = useCallback((next: Env) => {
    setEnv(next);
    setMode({ kind: 'compose' });
    setSendError(null);
  }, []);

  const handlePreview = useCallback(
    async (draft: DraftBroadcast) => {
      setSendError(null);

      // Synthetic hardcoded IDs aren't real DB rows — fall back to lat/lng.
      const useMarketId =
        !draft.customArea &&
        !!draft.marketId &&
        !draft.marketId.startsWith(HARDCODED_PREFIX);

      const req: AreaPreviewRequest = useMarketId
        ? { market_id: draft.marketId as string }
        : {
            center_lat: draft.centerLat,
            center_lng: draft.centerLng,
            radius_mi: draft.radiusMi,
          };

      const resp = await preview(env, req);
      if (resp) {
        setMode({ kind: 'preview', draft, preview: resp });
      }
    },
    [env, preview],
  );

  const handleSend = useCallback(async () => {
    if (mode.kind !== 'preview') return;
    setSendError(null);

    const { draft, preview: p } = mode;
    const useMarketId =
      !draft.customArea &&
      !!draft.marketId &&
      !draft.marketId.startsWith(HARDCODED_PREFIX);

    const req: SendAreaBroadcastRequest = {
      title: draft.title,
      body: draft.body,
      deep_link_url: draft.deepLinkUrl || undefined,
      center_lat: p.center_lat,
      center_lng: p.center_lng,
      radius_mi: p.radius_mi,
      market_id: useMarketId ? draft.marketId ?? undefined : undefined,
    };

    const resp = await send(env, req);
    if (resp) {
      setMode({ kind: 'compose' });
      setHistoryKey((k) => k + 1);
    } else {
      // useSendAreaBroadcast keeps its own error in state, but it's encapsulated
      // inside the hook closure. Surface a generic banner; PreviewPanel stays
      // open so the user can retry.
      setSendError('Failed to send broadcast. Please try again.');
    }
  }, [env, mode, send]);

  const handleEdit = useCallback(() => {
    setMode({ kind: 'compose' });
    setSendError(null);
  }, []);

  const composeKey = `${env}-${historyKey}`;

  return (
    <>
      {/* Toolbar: env switcher (matches BusinessPushNotificationsTab) */}
      <div className="flex items-center gap-2 sm:gap-4 mb-6 flex-wrap">
        <h2 className="text-lg font-semibold text-white mr-auto">
          Area Notifications
        </h2>

        <div className="flex items-center gap-1.5 p-1 bg-gray-800/80 border border-gray-700 rounded-xl">
          {(['dev', 'staging', 'prod'] as Env[]).map((e) => {
            const isConfigured = Boolean(getAdminApiUrl(e));
            const isActive = env === e;
            return (
              <button
                key={e}
                onClick={() => isConfigured && handleEnvChange(e)}
                disabled={!isConfigured}
                title={isConfigured ? undefined : 'Not configured'}
                className={`px-3 py-1 text-xs font-medium rounded-lg border transition-all capitalize ${
                  isActive
                    ? `${ENV_COLORS[e].pill} border-opacity-100`
                    : isConfigured
                      ? 'text-gray-400 border-transparent hover:text-gray-200 hover:bg-gray-700/60'
                      : 'text-gray-600 border-transparent cursor-not-allowed opacity-40'
                }`}
              >
                {e}
              </button>
            );
          })}
        </div>

        {env === 'prod' && (
          <span
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-xs font-semibold tracking-wide ${ENV_COLORS.prod.badge}`}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-red-400 animate-pulse" />
            PRODUCTION
          </span>
        )}
      </div>

      {/* Active state */}
      <div
        ref={activePanelRef}
        className="bg-gray-900 border border-gray-800 rounded-2xl p-5 sm:p-6 mb-8 scroll-mt-6"
      >
        {previewError && (
          <div className="mb-4">
            <ErrorBanner message={previewError} />
          </div>
        )}
        {sendError && (
          <div className="mb-4">
            <ErrorBanner message={sendError} />
          </div>
        )}

        {previewLoading && (
          <div className="py-16 text-center flex flex-col items-center gap-3">
            <Spinner size="lg" />
            <p className="text-sm text-gray-400">Computing audience…</p>
          </div>
        )}

        {!previewLoading && mode.kind === 'compose' && (
          <ComposeForm
            key={composeKey}
            markets={markets}
            onPreview={handlePreview}
          />
        )}

        {!previewLoading && mode.kind === 'preview' && (
          <PreviewPanel
            draft={mode.draft}
            preview={mode.preview}
            onEdit={handleEdit}
            onSend={handleSend}
          />
        )}

        {marketsLoading && mode.kind === 'compose' && !previewLoading && (
          <p className="mt-4 text-xs text-gray-500">Loading markets…</p>
        )}
      </div>

      {/* History */}
      <section>
        <h3 className="text-base font-semibold text-white mb-3">
          Recent broadcasts
        </h3>
        <div className="bg-gray-900 border border-gray-800 rounded-2xl overflow-hidden">
          <HistoryTable env={env} refreshKey={historyKey} />
        </div>
      </section>
    </>
  );
}
